import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { Eyebrow, Reveal, SectionShell } from "./Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlayerNews() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/news`);
        setItems(await res.json());
      } catch {
        setItems([]);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  return (
    <SectionShell id="news">
      <Reveal>
        <Eyebrow className="mb-5">— 08 / Player News</Eyebrow>
        <h2 className="max-w-3xl font-display text-4xl uppercase leading-[1.05] sm:text-5xl lg:text-6xl">
          Player <span className="text-volt">news.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Signings, tournament results and announcements from the SMG roster — straight
          from the team.
        </p>
      </Reveal>

      {loaded && items.length === 0 && (
        <Reveal>
          <div
            className="mt-14 flex items-center gap-4 rounded-3xl border border-white/10 bg-surface p-8"
            data-testid="news-empty-state"
          >
            <Megaphone className="h-6 w-6 shrink-0 text-volt" />
            <p className="text-sm leading-relaxed text-slate-400 sm:text-base">
              Player news is coming soon — signings, results and announcements will land
              here first.
            </p>
          </div>
        </Reveal>
      )}

      {items.length > 0 && (
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={item.id} delay={0.06 * i} className="h-full">
              <article
                className="flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-surface transition-all duration-300 hover:-translate-y-1.5 hover:border-volt/50 hover:shadow-[0_20px_60px_rgba(132,204,22,0.1)]"
                data-testid={`news-card-${item.id}`}
              >
                {item.image && (
                  <img
                    src={item.image.startsWith("http") ? item.image : `${API}/files/${item.image}`}
                    alt={item.title}
                    className="h-52 w-full border-b border-white/10 object-cover"
                    loading="lazy"
                  />
                )}
                <div className="flex flex-1 flex-col p-8">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      {item.date}
                    </span>
                    {item.athlete && (
                      <span className="rounded-full border border-volt/40 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-volt">
                        {item.athlete}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-5 font-display text-xl uppercase leading-snug text-white">
                    {item.title}
                  </h3>
                  <p className="mt-4 flex-1 whitespace-pre-line text-sm leading-relaxed text-slate-300">
                    {item.body}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      )}
    </SectionShell>
  );
}
